import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TeamSnapshotWithUser } from '@/api/type-response'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { EmptyView, Loading, Pagination, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { TeamRole } from '@/consts/team'
import { SnapStatus } from '@/models/snapshot'
import { SnapshotStorageBadge } from '@/pages/snapshots/components/SnapshotStorageBadge'
import { useTeamStore, useTeamUserRole } from '@/stores/team'

import { SnapshotTable } from '../components/SnapshotTable'
import { PAGE_SIZE } from '../constants'
import { useSnapshotList } from '../hooks/useSnapshotList'
import { useSnapshotOperations } from '../hooks/useSnapshotOperations'
import { useTeamSharedSnapshotStats } from '../hooks/useTeamSharedSnapshotStats'

export function SharedSnapshotView() {
  const { t } = useTranslation()
  const [selectedStatuses, setSelectedStatuses] = useState<(string | number)[]>(['all'])
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const teamStore = useTeamStore()
  const teamTokenData = teamStore.teamTokenData
  const userRole = useTeamUserRole()
  const isAdminOrOwner = userRole === TeamRole.Owner || userRole === TeamRole.Admin

  // Fetch team shared snapshot statistics
  // Admin/Owner have permission to call this API
  const { teamShared, refetch: refetchQuota } = useTeamSharedSnapshotStats({
    teamResID: teamTokenData?.teamResID,
  })
  const { snapshots, loading, page, total, fetchSnapshots } = useSnapshotList<TeamSnapshotWithUser>(
    {
      type: 'team-shared',
      teamResID: teamTokenData?.teamResID,
      selectedStatuses,
    },
  )

  const { removeFromShared, bulkRemoveFromShared } = useSnapshotOperations({
    teamResID: teamTokenData?.teamResID,
    onSuccess: () => {
      fetchSnapshots()
      refetchQuota()
    },
  })

  // Check if selected snapshots can be deleted (only Available status)
  const selectedSnapshotsInfo = useMemo(() => {
    const selected = snapshots.filter((s) => selectedIds.includes(s.snapshot.resID))
    const allAvailable = selected.every((s) => s.snapshot.status === SnapStatus.Finished)
    const nonAvailableCount = selected.filter(
      (s) => s.snapshot.status !== SnapStatus.Finished,
    ).length

    return {
      count: selected.length,
      canDelete: allAvailable,
      nonAvailableCount,
    }
  }, [selectedIds, snapshots])

  // Status filter options
  const statusOptions = [
    { label: t('snapshot.type.available', 'Available'), value: 'available' },
    { label: t('snapshot.type.restorable', 'Restorable'), value: 'restorable' },
  ]

  const handleBulkDelete = async () => {
    await bulkRemoveFromShared(selectedIds)
    setSelectedIds([])
  }

  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids)
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Breadcrumb Navigation */}
      <div className="border-b border-neutral-100 pb-4">
        <div className="flex items-center gap-3">
          <Link
            to={isAdminOrOwner ? '/team/snapshots/admin' : '/team/snapshots/member'}
            className="p-1 bg-panel-gray-100 rounded-md"
          >
            <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
          </Link>
          <div className="h6">
            <span className="text-tertiary">
              <Trans i18nKey="snapshot.breadcrumb.snapshots">Snapshots</Trans>
            </span>
            <span> / </span>
            <span>
              <Trans i18nKey="snapshot.breadcrumb.teamSnapshots">Team Snapshots</Trans>
            </span>
          </div>
          <SnapshotStorageBadge bytes={teamShared.usageBytes} />
        </div>
      </div>

      {/* Section 3: Filter + Table */}
      <div className="flex flex-col gap-4">
        {/* Status Filter + Bulk Actions */}
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-2">
            <label className="t-sb text-secondary">
              <Trans i18nKey="teamSnapshot.filter.status">Snapshot Status</Trans>
            </label>
            <Dropdown
              options={statusOptions}
              value={selectedStatuses}
              onChange={(value) => {
                const newValue = Array.isArray(value) ? value : [value]
                setSelectedStatuses(newValue.length === 0 ? ['all'] : newValue)
              }}
              multiple={true}
              showAllOption={true}
              placeholder="Select status"
              rootClassName="w-[200px]"
            />
          </div>

          {/* Bulk Actions */}
          <div className="ml-auto flex gap-3">
            {isAdminOrOwner && selectedIds.length > 0 && (
              <div>
                <Dropdown
                  rootClassName="t-sb bg-neutral-50 text-primary rounded-lg cursor-pointer"
                  className="h-11 px-4 border-none"
                  menuClassName="w-40"
                  options={[
                    {
                      label: 'Delete',
                      value: 'delete',
                      className: 't-sm text-error',
                    },
                  ]}
                  value=""
                  disabled={!selectedSnapshotsInfo.canDelete}
                  onChange={(value) => {
                    if (value === 'delete') {
                      handleBulkDelete()
                    }
                  }}
                  placeholder="Bulk Edit"
                />
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : snapshots.length === 0 ? (
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        ) : (
          <>
            <SnapshotTable
              snapshots={snapshots}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              variant="admin"
              showCheckbox={isAdminOrOwner}
              showOwner={true}
              ownerColumnTitle={<Trans i18nKey="teamSnapshot.shared.from">From</Trans>}
              detailLinkPath={(record) => `/team/snapshots/detail/${record.snapshot.resID}`}
              actionItems={
                isAdminOrOwner
                  ? (record) => [
                      {
                        label: (
                          <div className="text-error">
                            <Trans i18nKey="teamSnapshot.shared.remove">Delete</Trans>
                          </div>
                        ),
                        onClick: () => removeFromShared(record),
                        type: 'danger',
                        disabled: record.snapshot.status !== SnapStatus.Finished,
                      },
                    ]
                  : undefined
              }
            />
            <div className="flex justify-between items-center mt-4">
              <div className="t-sm text-secondary">
                {selectedIds.length > 0 ? (
                  <Trans
                    i18nKey="team.admin.selected"
                    values={{ count: selectedIds.length, total }}
                  >
                    Selected {selectedIds.length} / {total}
                  </Trans>
                ) : (
                  <span>&nbsp;</span>
                )}
              </div>
              <Pagination
                size={PAGE_SIZE}
                total={total}
                current={page}
                onChange={(newPage) => fetchSnapshots(newPage)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
